import {
  LucideCircleDotDashed,
  LucideLineSquiggle,
  LucideMousePointerSquareDashed,
  LucideTrash2,
  LucideWaypoints,
  LucideX,
} from 'lucide-react'
import type { Feature } from 'geojson'
import { useCallback, useEffect } from 'react'
import {
  aDrawMode,
  aDrawnFeature,
  aMapDrawIsOpen,
  aUser,
  useAtom,
  useAtomValue,
} from '~/atoms'
import {
  ControlTooltip,
  isStoredCircleFeature,
  useGoogleCircleDraw,
  useGoogleTerraDraw,
} from '~/components'
import { Button } from '~/components/ui'
import { cn } from '~/utils'
import { clearRememberedDrawnFeatureForBackNavigation } from '../../draw'

type ActiveDrawShapeMode = 'draw_polygon' | 'draw_circle' | 'draw_freehand'

function getActiveDrawShapeMode({
  drawMode,
  drawnFeature,
}: {
  drawMode: string | null
  drawnFeature: Feature | null
}): ActiveDrawShapeMode | null {
  if (
    drawMode === 'draw_polygon' ||
    drawMode === 'draw_circle' ||
    drawMode === 'draw_freehand'
  ) {
    return drawMode
  }

  if (!drawnFeature) return null

  if (drawMode !== 'simple_select') return null

  if (isStoredCircleFeature(drawnFeature)) {
    return 'draw_circle'
  }

  if (drawnFeature.properties?.appDrawMode === 'draw_freehand') {
    return 'draw_freehand'
  }

  return 'draw_polygon'
}

function isEditableKeyboardTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false

  if (target.isContentEditable) return true

  return Boolean(target.closest('input, textarea, select, [contenteditable]'))
}

export default function DrawControl({ className }: { className?: string }) {
  const user = useAtomValue(aUser)
  const [isDrawOpen, setIsDrawOpen] = useAtom(aMapDrawIsOpen)
  const [drawMode, setDrawMode] = useAtom(aDrawMode)
  const [drawnFeature, setDrawnFeature] = useAtom(aDrawnFeature)
  const { canDraw, clearDrawing } = useGoogleTerraDraw()
  const { canDrawCircle, clearCircle, startCircleDraw, stopCircleDraw } =
    useGoogleCircleDraw()

  useEffect(() => {
    if (drawnFeature && !isDrawOpen) {
      setIsDrawOpen(true)
    }
  }, [drawnFeature, isDrawOpen, setIsDrawOpen])

  const deleteFeature = useCallback(() => {
    clearRememberedDrawnFeatureForBackNavigation()
    clearDrawing()
    clearCircle()
    setDrawnFeature(null)
    setDrawMode(null)
  }, [clearCircle, clearDrawing, setDrawMode, setDrawnFeature])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (isEditableKeyboardTarget(e.target)) return

      if (e.key === 'Backspace' || e.key === 'Delete') {
        if (drawnFeature) {
          deleteFeature()
        }
      } else if (e.key === 'Escape' && drawMode) {
        deleteFeature()
      } else if (e.key === 'Enter' && drawMode && drawnFeature) {
        e.preventDefault()
        stopCircleDraw()
        setDrawMode(null)
        setIsDrawOpen(true)
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [
    deleteFeature,
    drawMode,
    drawnFeature,
    setDrawMode,
    setIsDrawOpen,
    stopCircleDraw,
  ])

  if (!user) {
    return null
  }

  const isReady = canDraw || canDrawCircle
  const activeDrawShapeMode = getActiveDrawShapeMode({
    drawMode,
    drawnFeature,
  })

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-md shadow-md ',
        isDrawOpen &&
          'bg-background/65 pr-2 backdrop-blur supports-[backdrop-filter]:bg-background/65',
        className
      )}>
      <ControlTooltip text="Start Drawing" side="bottom">
        <Button
          variant="map"
          size="map"
          disabled={!isReady}
          className="w-auto px-3 shadow-none"
          onClick={() => {
            if (isDrawOpen) {
              if (!drawnFeature && drawMode === null) {
                setDrawMode(null)
                setIsDrawOpen(false)
              }
            } else {
              setIsDrawOpen(true)
            }
          }}>
          <LucideMousePointerSquareDashed className="h-5 w-5" />
          <span className="ml-2 hidden xl:inline-flex">Draw</span>
        </Button>
      </ControlTooltip>
      {isDrawOpen && (
        <>
          <ControlTooltip text="Draw Polygon" side="bottom">
            <Button
              variant="map"
              size="map"
              disabled={!canDraw}
              className={cn(
                'h-7 w-7 shadow-none',
                activeDrawShapeMode === 'draw_polygon' &&
                  'bg-primary text-white'
              )}
              onClick={() => {
                deleteFeature()
                setDrawMode('draw_polygon')
                setIsDrawOpen(true)
              }}>
              <LucideWaypoints className="h-4 w-4" />
            </Button>
          </ControlTooltip>
          <ControlTooltip text="Draw Circle" side="bottom">
            <Button
              variant="map"
              size="map"
              disabled={!canDrawCircle}
              className={cn(
                'relative h-7 w-7 shadow-none',
                'hide-for-touch',
                activeDrawShapeMode === 'draw_circle' &&
                  'bg-primary text-white'
              )}
              onClick={() => {
                deleteFeature()
                startCircleDraw()
              }}>
              <LucideCircleDotDashed className="h-4 w-4" />
            </Button>
          </ControlTooltip>
          <ControlTooltip text="Draw Freehand Area" side="bottom">
            <Button
              variant="map"
              size="map"
              disabled={!canDraw}
              className={cn(
                'h-7 w-7 shadow-none',
                'hide-for-touch',
                activeDrawShapeMode === 'draw_freehand' &&
                  'bg-primary text-white'
              )}
              onClick={() => {
                deleteFeature()
                setDrawMode('draw_freehand')
                setIsDrawOpen(true)
              }}>
              <LucideLineSquiggle className="h-4 w-4" />
            </Button>
          </ControlTooltip>
          {drawnFeature && (
            <ControlTooltip text="Delete Drawing" side="bottom">
              <Button
                variant="map"
                size="map"
                className="h-7 w-7 shadow-none"
                onClick={deleteFeature}>
                <LucideTrash2 className="h-4 w-4" />
              </Button>
            </ControlTooltip>
          )}
          {drawMode && !drawnFeature && (
            <ControlTooltip text="Cancel Drawing" side="bottom">
              <Button
                variant="map"
                size="map"
                className="h-7 w-7 shadow-none"
                onClick={deleteFeature}>
                <LucideX className="h-4 w-4" />
              </Button>
            </ControlTooltip>
          )}
        </>
      )}
    </div>
  )
}
